import { ArrowRightIcon } from '@heroicons/react/20/solid';
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { motion } from 'framer-motion';
import { analyticsService } from 'src/services/analytics/analyticsService';
import {
  RFQ_BUILDER_CREATE_ERROR,
  RFQ_BUILDER_START_CLICKED
} from 'src/services/analytics/rfqBuilderEvents';
import { useMediaQuery } from 'src/shared/hooks';

import TypingText from 'src/shared/components/TypingText';
import { ButtonV2, Typography } from 'src/shared/components/ui';
import { isWebGLAvailable } from 'src/shared/components/WebGLErrorBoundary';

import { useCreateRfqBuilder } from '../hooks/useCreateRfqBuilder';
import BlobFallback from './BlobFallback';
import BlobV2 from './BlobV2';

const PLACEHOLDER_RECOMMENDATIONS = [
  'Chore coat, three button fastening. 100% organic cotton. 1000 units in dark blue, 1000 in black.',
  'Ceramic plate, 26 cm diameter, high-fire stoneware, glossy white food-safe glaze with cobalt blue rim, kiln-fired center logo. 2000 units.',
  'Grey acid wash dad hat. 1500 units. Brass buckle, embroidered white text on the front.'
];

interface RfqBuilderLandingProps {
  handleNext: () => void;
  generateRfq: (id?: number) => Promise<void>;
}

const RfqBuilderLanding: React.FC<RfqBuilderLandingProps> = ({
  handleNext,
  generateRfq
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const fadeOutTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const answerRef = useRef('');
  const previousPlaceholderRef = useRef('');
  const previousAnswerRef = useRef('');
  const previousPlaceholderIndexRef = useRef(0);
  const isInitialMountRef = useRef(true);
  const [showBlob, setShowBlob] = useState(false);
  const [fadeOutBlob, setFadeOutBlob] = useState(false);
  const [answer, setAnswer] = useState('');
  const [placeholderText, setPlaceholderText] = useState('');
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  const [fadeOutText, setFadeOutText] = useState('');
  const [fadeOutStartIndex, setFadeOutStartIndex] = useState(0);
  const isMobile = useMediaQuery();

  const handleTypingComplete = () => {
    setShowBlob(true);
  };

  const {
    mutate: createRfqBuilder,
    isPending,
    isSuccess
  } = useCreateRfqBuilder();
  const [, setSearchParams] = useSearchParams();

  useEffect(() => {
    answerRef.current = answer;
  }, [answer]);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = '180px';
      const scrollHeight = textareaRef.current.scrollHeight;
      const newHeight = Math.min(Math.max(scrollHeight, 180), 360);

      textareaRef.current.style.height = `${newHeight}px`;
    }
  }, [answer]);

  // Handle fade out when user starts typing or placeholder index changes
  useEffect(() => {
    const prevPlaceholder = previousPlaceholderRef.current;
    const currentPlaceholder = placeholderText;
    const prevAnswer = previousAnswerRef.current;
    const currentAnswer = answerRef.current;
    const prevIndex = previousPlaceholderIndexRef.current;
    const currentIndex = placeholderIndex;

    // Don't fade out if we're currently typing in a new placeholder (text is growing)
    const isTypingNewPlaceholder =
      currentPlaceholder.length > 0 &&
      prevPlaceholder.length >= 0 &&
      currentPlaceholder.length > prevPlaceholder.length &&
      (prevPlaceholder.length === 0 ||
        currentPlaceholder.startsWith(prevPlaceholder));

    // User just started typing (answer changed from empty to non-empty) and we had placeholder text
    const userStartedTyping = !prevAnswer && currentAnswer && prevPlaceholder;

    // Placeholder text was cleared (switching to new placeholder) and we had text before
    const placeholderCleared =
      prevPlaceholder && !currentPlaceholder && !currentAnswer;

    // Placeholder index changed (switching to new placeholder)
    const indexChanged = prevIndex !== currentIndex && prevPlaceholder;

    const shouldFadeOut =
      (userStartedTyping || (placeholderCleared && indexChanged)) &&
      !isTypingNewPlaceholder;

    if (shouldFadeOut && prevPlaceholder) {
      const textToFadeOut = prevPlaceholder;

      setFadeOutText(textToFadeOut);
      setFadeOutStartIndex(0);

      // Clear any existing fade out timeout
      if (fadeOutTimeoutRef.current) {
        clearTimeout(fadeOutTimeoutRef.current);
        fadeOutTimeoutRef.current = null;
      }

      const typingSpeed = 30; // milliseconds per character
      const fadeOutDelay = 3000; // milliseconds delay before fade-out starts
      let startIndex = 0;

      const fadeOutNextChar = () => {
        if (startIndex < textToFadeOut.length) {
          setFadeOutStartIndex(startIndex + 1);
          startIndex++;
          fadeOutTimeoutRef.current = setTimeout(fadeOutNextChar, typingSpeed);
        } else {
          setFadeOutText('');
          setFadeOutStartIndex(0);
        }
      };

      fadeOutTimeoutRef.current = setTimeout(fadeOutNextChar, fadeOutDelay);
    }

    // Clear fade out text if we're typing a new placeholder
    if (isTypingNewPlaceholder && fadeOutText) {
      setFadeOutText('');
      setFadeOutStartIndex(0);
      if (fadeOutTimeoutRef.current) {
        clearTimeout(fadeOutTimeoutRef.current);
        fadeOutTimeoutRef.current = null;
      }
    }

    // Update previous refs
    previousPlaceholderRef.current = placeholderText;
    previousAnswerRef.current = answerRef.current;
    previousPlaceholderIndexRef.current = placeholderIndex;

    return () => {
      if (fadeOutTimeoutRef.current) {
        clearTimeout(fadeOutTimeoutRef.current);
        fadeOutTimeoutRef.current = null;
      }
    };
  }, [placeholderText, answer, placeholderIndex]);

  useEffect(() => {
    // Clear any existing typing animation
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = null;
    }

    // Only animate placeholder when textarea is empty
    if (answerRef.current) {
      setPlaceholderText('');
      setFadeOutText('');
      setFadeOutStartIndex(0);

      return;
    }

    const currentText = PLACEHOLDER_RECOMMENDATIONS[placeholderIndex];
    const typingSpeed = 30; // milliseconds per character
    let charIndex = 0;

    const typeNextChar = () => {
      if (answerRef.current) {
        // User started typing, stop animation
        return;
      }

      if (charIndex < currentText.length) {
        setPlaceholderText(currentText.slice(0, charIndex + 1));
        charIndex++;
        typingTimeoutRef.current = setTimeout(typeNextChar, typingSpeed);
      } else {
        // Finished typing current recommendation, wait then move to next
        typingTimeoutRef.current = setTimeout(() => {
          if (!answerRef.current) {
            setPlaceholderIndex(
              (prevIndex) =>
                (prevIndex + 1) % PLACEHOLDER_RECOMMENDATIONS.length
            );
            setPlaceholderText('');
          }
        }, 0);
      }
    };

    // Start typing after a delay
    // If placeholder index changed, wait for fade-out to complete
    const hasPlaceholderChanged =
      previousPlaceholderIndexRef.current !== placeholderIndex &&
      previousPlaceholderRef.current;
    const delay = hasPlaceholderChanged
      ? 5000
      : isInitialMountRef.current
        ? 1000
        : previousPlaceholderRef.current
          ? 5000
          : 8000;

    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;
    }

    typingTimeoutRef.current = setTimeout(() => {
      if (!answerRef.current) {
        typeNextChar();
      }
    }, delay);

    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = null;
      }
    };
  }, [placeholderIndex, answer]);

  const handleNextWithAnimation = () => {
    analyticsService.trackEvent(RFQ_BUILDER_START_CLICKED, {
      description_length: answer.length
    });
    createRfqBuilder(
      { answer: answer },
      {
        onSuccess: (data) => {
          setFadeOutBlob(true);
          generateRfq(data.rfq_id);
          setTimeout(() => {
            setSearchParams({ rfq: data.rfq_id.toString(), step: 'intro' });
            handleNext();
          }, 1000);
        },
        onError: () => {
          analyticsService.trackEvent(RFQ_BUILDER_CREATE_ERROR, {
            description_length: answer.length
          });
        }
      }
    );
  };

  return (
    <div
      ref={containerRef}
      className="h-full max-h-full flex flex-col gap-6 relative mobile:justify-start mobile:pt-20 justify-center mobile:overflow-y-auto overflow-hidden"
    >
      <div className="relative z-50 mobile:px-6 px-[15%]" translate="no">
        <Typography
          as="h1"
          variant="h1"
          weight="bold"
          className="mobile:text-[32px] mobile:w-[80%] text-[40px] relative"
        >
          <span className="relative z-0">
            <TypingText
              fullText="What product are we sourcing today?"
              onComplete={handleTypingComplete}
            />
          </span>
        </Typography>
      </div>

      {showBlob && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: fadeOutBlob ? 0 : 1 }}
          transition={{
            duration: fadeOutBlob ? 1.2 : 6,
            delay: fadeOutBlob ? 0 : 1.2
          }}
          className="absolute z-40 inset-0"
        >
          {isWebGLAvailable() ? (
            <BlobV2
              speed={isPending ? 0.6 : 0.035}
              numBlobs={6}
              isolation={40}
              lookAt={
                isMobile ? { x: 0, y: 1200, z: 0 } : { x: -600, y: 200, z: 0 }
              }
              zoom={isMobile ? 3 : 1.5}
            />
          ) : (
            <BlobFallback
              isMobile={isMobile ?? false}
              variant="landing"
              isPending={isPending}
            />
          )}
        </motion.div>
      )}

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={showBlob ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
        transition={{
          duration: 0.6,
          delay: 0.3,
          ease: [0.25, 0.1, 0.25, 1]
        }}
        className="flex flex-col w-full items-start justify-start gap-4 relative z-50 mobile:px-6 px-[15%]"
      >
        <div className="relative max-w-[672px] w-full" translate="no">
          <textarea
            ref={textareaRef}
            value={answer}
            onChange={(e) => setAnswer(e.target.value)}
            autoFocus
            className="max-w-[672px] w-full p-4 resize-none outline-none overflow-y-auto"
            style={{
              background: 'rgba(255, 255, 255, 0.2)',
              borderRadius: '16px',
              boxShadow: '0 4px 30px rgba(0, 0, 0, 0.1)',
              backdropFilter: 'blur(5px)',
              WebkitBackdropFilter: 'blur(5px)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              minHeight: '180px',
              maxHeight: '360px'
            }}
          />
          {!answer && (placeholderText || fadeOutText) && (
            <div
              className="absolute inset-0 p-4 pointer-events-none flex items-start overflow-hidden"
              style={{
                color: 'rgba(0, 0, 0, 0.5)',
                whiteSpace: 'pre-wrap',
                wordWrap: 'break-word'
              }}
            >
              {fadeOutText ? (
                <span style={{ display: 'inline' }}>
                  {fadeOutText.split('').map((char, charIndex) => {
                    const isVisible = charIndex >= fadeOutStartIndex;

                    return (
                      <motion.span
                        key={`fadeout-${charIndex}`}
                        initial={{ opacity: 1 }}
                        animate={{ opacity: isVisible ? 1 : 0 }}
                        transition={{ duration: 0.3 }}
                        style={{ display: 'inline' }}
                      >
                        {char}
                      </motion.span>
                    );
                  })}
                </span>
              ) : (
                <span style={{ display: 'inline' }}>
                  {placeholderText.split('').map((char, charIndex) => (
                    <motion.span
                      key={`${placeholderIndex}-${charIndex}`}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.3 }}
                      style={{ display: 'inline' }}
                    >
                      {char}
                    </motion.span>
                  ))}
                </span>
              )}
            </div>
          )}
        </div>
        <Typography className="ml-1 text-gray-5 italic mb-2" variant="body-2">
          More detail helps us match you with the right manufacturers.
        </Typography>
        <ButtonV2
          onPress={handleNextWithAnimation}
          isDisabled={isPending || !answer || isSuccess}
          isLoading={isPending}
          size="lg"
          endContent={<ArrowRightIcon className="size-4 pointer-events-none" />}
          className="h-14 rounded-full px-6 w-32 backdrop-blur-2xl opacity-90"
        >
          Start
        </ButtonV2>
      </motion.div>
    </div>
  );
};

export default RfqBuilderLanding;
